import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Table from "src/componets/Table";
import { useAppSelector, useAppDispatch } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { RateInterface } from "src/store/callrecorder/ratesSlice";
import { fetchRates } from "src/actions/callrecorder/ratesActions";
import { queryParams } from "src/helpers/UrlHelpers";
import { formattedDate } from "src/helpers/DateHelpers";
import Pagination from "src/componets/Pagination";
import OptionalLink from "src/componets/OptionalLink";
import callrecorderUrls from "src/configs/callrecorder/callrecorderUrls";
import { selectProviderEntities } from "src/store/callrecorder/providersSlice";

interface ListTableInterface {
  className?: string;
  limit?: number;
  filterParams?: object;
}

const _ListTable = ({ className, limit = 42, filterParams }: ListTableInterface) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const currentPage = queryParams.getInt(searchParams, 'page') || 1;
  const ids = useAppSelector(state => state.callrecorder.rates.ids);
  const entities = useAppSelector(state => state.callrecorder.rates.entities);
  const count = useAppSelector(state => state.callrecorder.rates.count);
  const providerEntities = useAppSelector(selectProviderEntities);
  const data = ids ? ids.map(id => entities[id]) : [];

  const urlTerm = searchParams.get('term') || '';
  const urlSearchBy = searchParams.get('search_by') || '';
  const urlOrderBy = searchParams.get('order_by') || '';
  const urlOrder = searchParams.get('order') || '';

  const params = useMemo(() => ({
    limit,
    offset: (currentPage - 1) * limit,
    ...filterParams,
    ...(urlTerm ? { term: urlTerm, search_by: urlSearchBy } : {}),
    ...(urlOrderBy ? { order_by: urlOrderBy, order: urlOrder } : {})
  }), [limit, currentPage, filterParams, urlTerm, urlSearchBy, urlOrderBy, urlOrder]);

  useEffect(() => {
    if (filterParams) {
      dispatch(fetchRates(params));
    }
  }, [params, filterParams, dispatch]);

  const columns: ColumnsType<RateInterface> = [
    {
      key: 'id',
      title: 'ID',
      render: (_, { id, url }) => (
        <OptionalLink link={url}>
          <small>{`#${id}`}</small>
        </OptionalLink>
      ),
      width: 70
    },

    {
      key: 'provider_id',
      title: 'Provider',
      render: (_, { provider_id }) => {
        const provider = providerEntities[provider_id];
        return provider ? (
          <OptionalLink link={callrecorderUrls.providerPath(provider_id)}>
            <small>{provider.name}</small>
          </OptionalLink>
        ) : <small>{`#${provider_id}`}</small>;
      },
      width: 120
    },

    {
      key: 'prefix',
      title: 'Prefix',
      render: (_, { prefix }) => <small>{prefix}</small>,
      width: 100
    },

    {
      key: 'src_prefix',
      title: 'Src Prefix',
      render: (_, { src_prefix }) => src_prefix ? <small>{src_prefix}</small> : '—',
      width: 100
    },

    {
      key: 'cost_per_min',
      title: 'Cost/Min',
      render: (_, { cost_per_min, iso }) => (
        <small>{cost_per_min}</small>
      ),
      width: 100
    },

    {
      key: 'raw_cost',
      title: 'Raw Cost',
      render: (_, { raw_cost }) => raw_cost ? <small>{raw_cost}</small> : '—',
      width: 80
    },

    {
      key: 'iso',
      title: 'Country',
      render: (_, { iso }) => iso || '—',
      width: 80
    },

    {
      key: 'updated_at',
      title: 'Updated',
      render: (_, { updated_at }) => <small>{formattedDate(updated_at).dateTime}</small>,
      width: 125
    },
  ];

  return (
    <div className={className}>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
        search_keys={['prefix', 'src_prefix', 'iso', 'notes']}
        sort_keys={['id', 'prefix', 'src_prefix', 'cost_per_min', 'raw_cost', 'updated_at']}
      />

      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  )
}

export default _ListTable;
